/* ==========================================================================
   FILE: pages/DashboardPage.jsx
   IAMONEAI - Main Dashboard Page
   Three-panel dashboard for authenticated users
   
   UPDATED: December 7, 2025 - Now using StreamingChatContainer (v3.0)
   ========================================================================== */

import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppLayout } from '../global/layouts';
import { useApp } from '../global/context/AppContext';
import { useAPI } from '../global/hooks/useAPI';

// Module imports
import { ProfileCard, BlockchainStatus } from '../modules/identity';
import { StreamingChatContainer } from '../modules/chat/components/StreamingChatContainer';
import { SettingsPanel } from '../modules/brain';
import { MemoryList, GoalList, AnalyticsPanel, MIND_TABS } from '../modules/mind';

import './DashboardPage.css';

// ==========================================================================
// DASHBOARD PAGE COMPONENT
// ==========================================================================

function DashboardPage() {
  const navigate = useNavigate();
  const { user, iin, brain, logout, updateBrain, activeContext, setActiveContext } = useApp();
  const api = useAPI();

  // State
  const [activeTab, setActiveTab] = useState(MIND_TABS?.MEMORIES || 'memories');
  const [memories, setMemories] = useState([]);
  const [goals, setGoals] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [showSettings, setShowSettings] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);

  // Redirect if not logged in
  useEffect(() => {
    if (!iin) {
      navigate('/auth?mode=login');
    }
  }, [iin, navigate]);

  // Load initial data
  useEffect(() => {
    if (iin) {
      loadDashboardData();
    }
  }, [iin]);

  // ==========================================================================
  // DATA LOADING
  // ==========================================================================

  const loadDashboardData = useCallback(async () => {
    if (!iin) return;
    
    setIsLoadingData(true);
    try {
      // Load memories
      const memoriesResult = await api.getMemories(iin);
      if (memoriesResult?.memories) {
        // Normalize memory content - handle object vs string
        const normalizedMemories = memoriesResult.memories.map(m => ({
          ...m,
          // If content is an object, use summary or raw, otherwise use content as-is
          content: typeof m.content === 'object' 
            ? (m.content?.summary || m.content?.raw || JSON.stringify(m.content))
            : m.content,
          // Preserve original for reference
          _originalContent: m.content,
        }));
        
        setMemories(normalizedMemories);
        // Separate goals from memories
        const goalMemories = normalizedMemories.filter(m => m.type === 'goal');
        setGoals(goalMemories);
      }

      // Load analytics (if available)
      try {
        const currentPeriod = new Date().toISOString().slice(0, 7);
        const analyticsResult = await api.getAnalytics?.(iin, currentPeriod);
        if (analyticsResult) {
          setAnalytics(analyticsResult);
        }
      } catch (analyticsError) {
        console.log('Analytics not available:', analyticsError);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setIsLoadingData(false);
    }
  }, [iin, api]);

  // ==========================================================================
  // HANDLERS
  // ==========================================================================

  const handleLogout = useCallback(() => {
    logout();
    navigate('/');
  }, [logout, navigate]);

  const handleBrainUpdate = useCallback((updatedBrain) => {
    updateBrain(updatedBrain);
    setShowSettings(false);
  }, [updateBrain]);

  const handleContextChange = useCallback((newContext) => {
    if (newContext && setActiveContext) {
      setActiveContext(newContext);
    }
  }, [setActiveContext]);

  // Handle when streaming chat completes a message
  const handleMessageComplete = useCallback((data) => {
    // Refresh data if memories were saved or goals created
    if (data.memories_saved?.length > 0 || data.scheduled?.length > 0) {
      loadDashboardData();
    }
    
    // Handle context switch from chat
    if (data.intention === 'context.switch' && data.context) {
      handleContextChange(data.context);
    }
  }, [loadDashboardData, handleContextChange]);

  const handleGoalStatusChange = useCallback(async (goal, newStatus) => {
    // Update goal status via API
    try {
      await api.chat(
        newStatus === 'done' 
          ? `I completed my goal: ${goal.content}`
          : `${newStatus} my goal: ${goal.content}`,
        iin
      );
      // Refresh data
      loadDashboardData();
    } catch (error) {
      console.error('Error updating goal:', error);
    }
  }, [iin, api, loadDashboardData]);

  const handleMemoryArchive = useCallback(async (memory) => {
    // Archive memory via API
    try {
      // TODO: Implement archive API call
      setMemories(prev => prev.filter(m => m.id !== memory.id));
    } catch (error) {
      console.error('Error archiving memory:', error);
    }
  }, []);

  // ==========================================================================
  // RENDER PANELS
  // ==========================================================================

  // Left Panel: Identity/Profile
  const renderIdentityPanel = () => (
    <div className="dashboard-panel dashboard-identity-panel">
      <ProfileCard
        user={user}
        brain={brain}
        iin={iin}
        onEdit={() => setShowSettings(true)}
        onLogout={handleLogout}
      />
      
      <BlockchainStatus
        isAnchored={user?.blockchainAnchored}
        txHash={user?.blockchainTxHash}
        network="Polygon Amoy"
        anchoredAt={user?.blockchainAnchoredAt}
        identityHash={user?.identityHash}
      />
    </div>
  );

  // Center Panel: Chat (NOW WITH STREAMING!)
  const renderChatPanel = () => (
    <div className="dashboard-panel dashboard-chat-panel">
      <StreamingChatContainer
        iin={iin}
        context={activeContext || brain?.activeContext || 'personal'}
        onMessageComplete={handleMessageComplete}
        onContextChange={handleContextChange}
      />
    </div>
  );

  // Right Panel: Mind (Memories/Goals/Analytics)
  const renderMindPanel = () => (
    <div className="dashboard-panel dashboard-mind-panel">
      {/* Tab Navigation */}
      <div className="mind-tabs">
        <button
          className={`mind-tab ${activeTab === 'memories' ? 'mind-tab-active' : ''}`}
          onClick={() => setActiveTab('memories')}
        >
          🧠 Memories
        </button>
        <button
          className={`mind-tab ${activeTab === 'goals' ? 'mind-tab-active' : ''}`}
          onClick={() => setActiveTab('goals')}
        >
          🎯 Goals
        </button>
        <button
          className={`mind-tab ${activeTab === 'analytics' ? 'mind-tab-active' : ''}`}
          onClick={() => setActiveTab('analytics')}
        >
          📊 Analytics
        </button>
      </div>

      {/* Tab Content */}
      <div className="mind-content">
        {activeTab === 'memories' && (
          <MemoryList
            memories={memories.filter(m => m.type !== 'goal')}
            loading={isLoadingData}
            onRefresh={loadDashboardData}
            onArchive={handleMemoryArchive}
          />
        )}

        {activeTab === 'goals' && (
          <GoalList
            goals={goals}
            loading={isLoadingData}
            onRefresh={loadDashboardData}
            onStatusChange={handleGoalStatusChange}
          />
        )}

        {activeTab === 'analytics' && (
          <AnalyticsPanel
            analytics={analytics}
            brain={brain}
          />
        )}
      </div>
    </div>
  );

  // ==========================================================================
  // MAIN RENDER
  // ==========================================================================

  // Don't render if not logged in
  if (!iin) {
    return null;
  }

  return (
    <>
      <AppLayout
        identityPanel={renderIdentityPanel()}
        chatPanel={renderChatPanel()}
        mindPanel={renderMindPanel()}
        header={null}
      />

      {/* Settings Modal */}
      {showSettings && (
        <div className="settings-overlay" onClick={() => setShowSettings(false)}>
          <div className="settings-modal" onClick={e => e.stopPropagation()}>
            <SettingsPanel
              iin={iin}
              brain={brain}
              onUpdate={handleBrainUpdate}
              onClose={() => setShowSettings(false)}
            />
          </div>
        </div>
      )}
    </>
  );
}

// ==========================================================================
// EXPORTS
// ==========================================================================

export default DashboardPage;
